import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, FileText, TestTube, User, LogOut, Heart, Plus, X, Save, Tag, CreditCard, Shield, Edit3, Check, Clock, Bell, ChevronLeft, ChevronRight, MessageSquare, Eye, Menu, Download, Activity, Lock, KeyRound, AlertCircle, CheckCircle, Loader2, Edit2, ClipboardList, ChevronDown, ChevronUp, Home, Star, Users, Upload, Receipt, Video, Printer, Send, Phone } from 'lucide-react';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';
import MessageBell from '../components/MessageBell';
import ProfilePhotoUploader from '../components/common/ProfilePhotoUploader';
import GuidedAnamnesis from '../components/GuidedAnamnesis';
import Breadcrumb from '../components/Breadcrumb';
import ReviewForm from '../components/ReviewForm';
import ReviewRequestPopup from '../components/ReviewRequestPopup';
import FamilyManager from '../components/patient/FamilyManager';
import DocumentManager from '../components/patient/DocumentManager';
import PaymentManager from '../components/patient/PaymentManager';
import GDPRManager from '../components/patient/GDPRManager';
import TelemedicineBooking from '../components/patient/TelemedicineBooking';
import SlotRecoveryMessages from '../components/patient/SlotRecoveryMessages';
import { PatientProtocolsView } from '../components/PreventionProtocols';
import PatientMessagesTab from '../components/PatientMessagesTab';
import VideoConsultManager from '../components/VideoConsultManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Blood type options
const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Sconosciuto'];
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

// Tag colors for timeline
const TAG_COLORS = {
  '#miopia': 'bg-blue-100 text-blue-700',
  '#ipermetropia': 'bg-purple-100 text-purple-700',
  '#presbiopia': 'bg-amber-100 text-amber-700',
  '#astigmatismo': 'bg-indigo-100 text-indigo-700',
  '#glaucoma': 'bg-red-100 text-red-700',
  '#cataratta': 'bg-orange-100 text-orange-700',
  '#maculopatia': 'bg-pink-100 text-pink-700',
  '#laser': 'bg-emerald-100 text-emerald-700',
  '#occhiosecco': 'bg-sky-100 text-sky-700',
  '#lenti': 'bg-sky-100 text-sky-700',
  'default': 'bg-gray-100 text-[#2A9FCB]'
};

const getTagColor = (tag) => {
  const normalizedTag = tag.toLowerCase().replace(/\s+/g, '');
  return TAG_COLORS[normalizedTag] || TAG_COLORS['default'];
};

export const PatientDashboard = () => {
  const [user, setUser] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [patientRecord, setPatientRecord] = useState(null);
  const [activeTab, setActiveTab] = useState('appointments');

  // Set default tab based on role
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      if (parsed.role !== 'patient') {
        setActiveTab('profile');
      }
    }
  }, []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle URL tab parameter
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && ['appointments', 'telemedicine', 'anamnesis', 'records', 'timeline', 'profile', 'privacy', 'payments', 'family', 'documents'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Reschedule modal state
  const [rescheduleModal, setRescheduleModal] = useState({ open: false, appointment: null });
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Pending confirmations
  const [pendingConfirmations, setPendingConfirmations] = useState([]);
  
  // Saved anamnesis reports from chatbot
  const [savedAnamnesisReports, setSavedAnamnesisReports] = useState([]);
  const [expandedReport, setExpandedReport] = useState(null);
  
  // Concluded visits
  const [concludedVisits, setConcludedVisits] = useState([]);
  
  // Modale invio referto via SMS
  const [shareSmsModal, setShareSmsModal] = useState({ show: false, visitId: null, visitDate: '', phone: '', recipientName: '', sending: false });
  
  // Profile integration states (from PatientProfile)
  const [timeline, setTimeline] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ old: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Guided Anamnesis modal
  const [showGuidedAnamnesis, setShowGuidedAnamnesis] = useState(false);
  
  // Google Calendar sync modal state
  const [calendarSyncModal, setCalendarSyncModal] = useState({
    show: false,
    loading: false,
    syncData: null
  });
  
  // Unread messages count for badge
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  
  // Review request popup
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [hasReviewRequests, setHasReviewRequests] = useState(false);
  const [expandedReportId, setExpandedReportId] = useState(null);

  // Anamnesis form state
  const [anamnesis, setAnamnesis] = useState({
    blood_type: 'Sconosciuto',
    allergies: [],
    chronic_conditions: [],
    current_medications: [],
    family_history: '',
    physiological_anamnesis: '',
    pathological_anamnesis: ''
  });

  // Input states for array fields
  const [newAllergy, setNewAllergy] = useState('');
  const [newCondition, setNewCondition] = useState('');
  const [newMedication, setNewMedication] = useState('');

  // Gestione redirect dopo pagamento Stripe
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      const invoiceId = urlParams.get('invoice');
      const sessionId = urlParams.get('session_id');
      
      if (paymentStatus === 'success') {
        toast.success('Pagamento completato con successo!');
        // Rimuovi i parametri dall'URL
        window.history.replaceState({}, document.title, '/dashboard/patient');
        // Vai alla tab pagamenti per mostrare la fattura aggiornata
        setActiveTab('payments');
      } else if (paymentStatus === 'cancelled') {
        toast.error('Pagamento annullato');
        window.history.replaceState({}, document.title, '/dashboard/patient');
      }
    };
    
    checkPaymentStatus();
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/auth');
      return;
    }
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    loadData();
    
    // Auto-refresh appuntamenti ogni 30 secondi per sincronizzazione real-time
    const refreshInterval = setInterval(() => {
      loadData();
    }, 30000);
    
    return () => clearInterval(refreshInterval);
  }, [navigate]);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [apptRes, recordsRes, testsRes, patientRecordRes, anamnesisReportsRes, concludedVisitsRes] = await Promise.all([
        axios.get(`${API}/appointments`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/medical-records`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/test-results`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/my-patient-record`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/chat/anamnesis-reports`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/my-concluded-visits`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      // Trasforma gli appuntamenti per compatibilità con il frontend
      const rawAppointments = apptRes.data || [];
      const transformedAppointments = rawAppointments.map(apt => ({
        ...apt,
        date: apt.appointment_date ? apt.appointment_date.split('T')[0] : apt.date,
        time: apt.appointment_date ? apt.appointment_date.split('T')[1]?.substring(0, 5) : apt.time
      }));
      setAppointments(transformedAppointments);
      
      setMedicalRecords(recordsRes.data || []);
      setTestResults(testsRes.data || []);
      setSavedAnamnesisReports(anamnesisReportsRes.data || []);
      setConcludedVisits(concludedVisitsRes.data || []);
      
      if (patientRecordRes.data) {
        setPatientRecord(patientRecordRes.data);
        setAnamnesis({
          blood_type: patientRecordRes.data.blood_type || 'Sconosciuto',
          allergies: patientRecordRes.data.allergies || [],
          chronic_conditions: patientRecordRes.data.chronic_conditions || [],
          current_medications: patientRecordRes.data.current_medications || [],
          family_history: patientRecordRes.data.family_history || '',
          physiological_anamnesis: patientRecordRes.data.physiological_anamnesis || '',
          pathological_anamnesis: patientRecordRes.data.pathological_anamnesis || ''
        });
      }
      
      // Build timeline from appointments and tests
      buildTimeline(apptRes.data || [], testsRes.data || []);
      
      // Load pending confirmations
      loadPendingConfirmations();
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadPendingConfirmations = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/appointments/pending-confirmations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingConfirmations(res.data.pending_confirmations || []);
    } catch (error) {
      console.error('Error loading confirmations:', error);
    }
  };

  // Carica conteggio messaggi non letti
  const loadUnreadMessages = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadMessagesCount(res.data.count || 0);
    } catch (error) {
      console.error('Error loading unread messages:', error);
    }
  };

  // Carica messaggi non letti ogni 30 secondi
  useEffect(() => {
    loadUnreadMessages();
    const interval = setInterval(loadUnreadMessages, 30000);
    return () => clearInterval(interval);
  }, []);

  // Carica richieste di recensione post-visita
  const loadReviewRequests = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/my-review-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data && res.data.length > 0) {
        setHasReviewRequests(true);
        // Mostra popup solo se non è stato già mostrato in questa sessione
        const lastShown = sessionStorage.getItem('reviewPopupShown');
        if (!lastShown) {
          setShowReviewPopup(true);
          sessionStorage.setItem('reviewPopupShown', 'true');
        }
      }
    } catch (error) {
      console.error('Error loading review requests:', error);
    }
  };

  // Carica le richieste di recensione al mount
  useEffect(() => {
    const timer = setTimeout(() => {
      loadReviewRequests();
    }, 2000); // Delay di 2 secondi per non sovrapporre al caricamento iniziale
    return () => clearTimeout(timer);
  }, []);

  // ========== PROFILE HELPER FUNCTIONS ==========

  const buildTimeline = (appts, tests) => {
    const events = [];
    
    // Add completed appointments
    appts.filter(a => a.status === 'completed' || new Date(a.appointment_date) < new Date())
      .forEach(a => {
        events.push({
          id: a.id,
          type: 'appointment',
          date: a.appointment_date,
          title: a.reason || 'Visita oculistica',
          description: `Visita con ${a.doctor_name || 'Dr. PRIME VISION'}`,
          status: a.status,
          tags: extractTags(a.reason)
        });
      });
    
    // Add test results
    tests.forEach(t => {
      events.push({
        id: t.id,
        type: 'test',
        date: t.completed_at || t.created_at,
        title: t.test_type || 'Test visivo',
        description: `Risultato: ${t.result || 'Completato'}`,
        status: 'completed',
        tags: [t.test_type ? `#${t.test_type.toLowerCase()}` : '#test']
      });
    });
    
    // Sort by date descending
    events.sort((a, b) => new Date(b.date) - new Date(a.date));
    setTimeline(events);
  };

  const extractTags = (text) => {
    if (!text) return [];
    const tags = [];
    const keywords = ['miopia', 'ipermetropia', 'presbiopia', 'astigmatismo', 'glaucoma', 
                      'cataratta', 'maculopatia', 'laser', 'occhio secco', 'lenti', 'controllo'];
    keywords.forEach(kw => {
      if (text.toLowerCase().includes(kw)) {
        tags.push(`#${kw.replace(' ', '')}`);
      }
    });
    return tags.length > 0 ? tags : ['#visita'];
  };

  const getAllTags = () => {
    const tagSet = new Set();
    timeline.forEach(event => {
      event.tags?.forEach(tag => tagSet.add(tag));
    });
    if (patientRecord?.conditions) {
      patientRecord.conditions.forEach(c => tagSet.add(`#${c.toLowerCase().replace(/\s+/g, '')}`));
    }
    return Array.from(tagSet);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Profile editing
  const startEditing = () => {
    setEditForm({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone: user?.phone || '',
      codice_fiscale: user?.codice_fiscale || '',
      blood_type: user?.blood_type || '',
      date_of_birth: user?.date_of_birth || '',
      place_of_birth: user?.place_of_birth || '',
      gender: user?.gender || '',
      address_street: user?.address_street || '',
      address_number: user?.address_number || '',
      address_zip: user?.address_zip || '',
      address_city: user?.address_city || ''
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const saveProfile = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.put(`${API}/profile`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        const updatedUser = { ...user, ...editForm };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        toast.success('Profilo aggiornato con successo!');
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Errore durante il salvataggio. Riprova.');
    } finally {
      setSaving(false);
    }
  };

  // Password change
  const handleChangePassword = async () => {
    if (!passwordForm.old || !passwordForm.new || !passwordForm.confirm) {
      toast.error('Compila tutti i campi');
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('Le password non corrispondono');
      return;
    }
    if (passwordForm.new.length < 6) {
      toast.error('La nuova password deve essere di almeno 6 caratteri');
      return;
    }

    setChangingPassword(true);
    const token = localStorage.getItem('token');
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: passwordForm.old,
        new_password: passwordForm.new
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password cambiata con successo!');
      setPasswordForm({ old: '', new: '', confirm: '' });
      setShowPasswordModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  // ========== GOOGLE CALENDAR SYNC ==========
  
  const syncGoogleCalendar = async () => {
    const token = localStorage.getItem('token');
    setCalendarSyncModal({ show: true, loading: true, syncData: null });
    
    try {
      const res = await axios.get(`${API}/calendar/sync/setup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarSyncModal({ show: true, loading: false, syncData: res.data });
    } catch (error) {
      console.error('Error setting up calendar sync:', error);
      toast.error('Errore nella configurazione della sincronizzazione');
      setCalendarSyncModal({ show: false, loading: false, syncData: null });
    }
  };

  const openGoogleCalendarSync = () => {
    if (calendarSyncModal.syncData?.google_calendar_url) {
      window.open(calendarSyncModal.syncData.google_calendar_url, '_blank');
      toast.success('🗓️ Reindirizzamento a Google Calendar...');
    }
  };

  const copyFeedUrl = () => {
    if (calendarSyncModal.syncData?.feed_url) {
      navigator.clipboard.writeText(calendarSyncModal.syncData.feed_url);
      toast.success('📋 URL copiato negli appunti!');
    }
  };

  // ========== RESCHEDULE FUNCTIONS ==========
  
  const openRescheduleModal = async (appointment) => {
    setRescheduleModal({ open: true, appointment });
    setRescheduleDate('');
    setRescheduleTime('');
    setAvailableSlots([]);
    // Load calendar for current month
    await loadCalendarForReschedule(currentMonth, currentYear);
  };

  const loadCalendarForReschedule = async (month, year) => {
    try {
      const res = await axios.get(`${API}/appointments/calendar?month=${month}&year=${year}`);
      setCalendarData(res.data.days || []);
    } catch (error) {
      console.error('Error loading calendar:', error);
    }
  };

  const loadSlotsForDate = async (date) => {
    setLoadingSlots(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/appointments/slots?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableSlots(res.data.available_slots || []);
    } catch (error) {
      console.error('Error loading slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = async (date) => {
    setRescheduleDate(date);
    setRescheduleTime('');
    await loadSlotsForDate(date);
  };

  const prevMonth = () => {
    let newMonth = currentMonth - 1;
    let newYear = currentYear;
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    loadCalendarForReschedule(newMonth, newYear);
  };

  const nextMonth = () => {
    let newMonth = currentMonth + 1;
    let newYear = currentYear;
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    loadCalendarForReschedule(newMonth, newYear);
  };

  const submitReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) {
      toast.error('Seleziona data e orario');
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      await axios.patch(
        `${API}/appointments/${rescheduleModal.appointment.id}/reschedule`,
        { new_date: rescheduleDate, new_time_slot: rescheduleTime },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Appuntamento modificato con successo!');
      setRescheduleModal({ open: false, appointment: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella modifica');
    }
  };

  // ========== CONFIRMATION FUNCTIONS ==========
  
  const confirmAppointment = async (appointmentId, confirmed) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/appointments/${appointmentId}/confirm`,
        { confirmed, confirmation_method: 'app' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(confirmed ? 'Appuntamento confermato! Il medico è stato notificato.' : 'Risposta inviata');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella conferma');
    }
  };

  const cancelAppointment = async (appointmentId) => {
    if (!window.confirm('Sei sicuro di voler cancellare questo appuntamento?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Appuntamento cancellato');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella cancellazione');
    }
  };

  const saveAnamnesis = async () => {
    setSaving(true);
    setSaveMessage('');
    const token = localStorage.getItem('token');
    
    try {
      await axios.put(`${API}/my-anamnesis`, anamnesis, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveMessage('Anamnesi salvata con successo!');
      // Reload data to get updated record
      loadData();
    } catch (error) {
      console.error('Error saving anamnesis:', error);
      setSaveMessage('Errore nel salvataggio. Riprova.');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const addToArray = (field, value, setter) => {
    if (value.trim()) {
      setAnamnesis(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setter('');
    }
  };

  const removeFromArray = (field, index) => {
    setAnamnesis(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const scheduledAppts = appointments.filter(a => a.status === 'scheduled');
  const completedAppts = appointments.filter(a => a.status === 'completed');
  const missedAppts = appointments.filter(a => a.status === 'missed');

  // Get diagnosis tags from patient record
  const diagnosisTags = patientRecord?.diagnosis_tags || [];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
      {/* Mobile Header with Hamburger - Luxury Style */}
      <div className="lg:hidden flex items-center justify-between p-4 bg-white border-b border-gray-100 shadow-sm">
        <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <img src="/logo-primevision-small.png" alt="PRIME VISION" className="w-10 h-10 object-contain" />
          <p className="text-xs tracking-wider mt-1" style={{color: '#666666', fontFamily: "'Manrope', sans-serif"}}>{user?.role === 'patient' ? 'Area Paziente' : user?.role === 'doctor' ? 'Area Medico' : user?.role === 'admin' ? 'Area Amministrativa' : 'Profilo'}</p>
        </Link>
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-full hover:bg-[#E6F4FA] transition-all duration-300"
          style={{color: '#2A9FCB'}}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu Overlay - Luxury Style */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}>
          <div 
            className="absolute top-0 right-0 w-80 h-full bg-white p-8 overflow-y-auto shadow-2xl animate-slide-in-right"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-10">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <img src="/logo-primevision-small.png" alt="PRIME VISION" className="w-10 h-10 object-contain" />
                <p className="text-xs tracking-wider mt-1" style={{color: '#666666', fontFamily: "'Manrope', sans-serif"}}>{user?.role === 'patient' ? 'Area Paziente' : user?.role === 'doctor' ? 'Area Medico' : user?.role === 'admin' ? 'Area Amministrativa' : 'Profilo'}</p>
              </Link>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-full hover:bg-[#E6F4FA] transition-all duration-300" style={{color: '#2A9FCB'}}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="space-y-3">
              {user?.role === 'patient' && (
                <button onClick={() => { navigate('/my-card'); setMobileMenuOpen(false); }} className="w-full flex items-center space-x-4 px-5 py-4 rounded-xl hover:bg-[#E6F4FA] transition-all duration-300 group" style={{color: '#1A1A1A'}}>
                  <CreditCard className="w-5 h-5 text-[#2A9FCB] group-hover:scale-110 transition-transform" /><span style={{fontFamily: "'Manrope', sans-serif"}}>Tessera Sanitaria</span>
                </button>
              )}
              <button onClick={() => { navigate('/tests'); setMobileMenuOpen(false); }} className="w-full flex items-center space-x-4 px-5 py-4 rounded-xl hover:bg-[#E6F4FA] transition-all duration-300 group" style={{color: '#1A1A1A'}}>
                <TestTube className="w-5 h-5 text-[#2A9FCB] group-hover:scale-110 transition-transform" /><span style={{fontFamily: "'Manrope', sans-serif"}}>Test Visivi</span>
              </button>
            </nav>

            <div className="mt-10 pt-6 border-t border-gray-100">
              <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="w-full flex items-center space-x-4 px-5 py-4 rounded-xl hover:bg-red-50 transition-all duration-300" style={{color: '#EF4444'}}>
                <LogOut className="w-5 h-5" /><span style={{fontFamily: "'Manrope', sans-serif"}}>Esci</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar - Luxury Style */}
      <div className="hidden lg:flex lg:flex-col dashboard-sidebar w-72 p-8 flex-shrink-0 bg-white border-r border-gray-100">
        <Link to="/" className="flex items-center gap-4 mb-12 hover:opacity-80 transition-opacity">
          <img src="/logo-primevision-small.png" alt="PRIME VISION" className="w-14 h-14 object-contain" />
          <p className="text-xs tracking-wider mt-1" style={{color: '#666666', fontFamily: "'Manrope', sans-serif"}}>{user?.role === 'patient' ? 'Area Paziente' : user?.role === 'doctor' ? 'Area Medico' : user?.role === 'admin' ? 'Area Amministrativa' : 'Profilo'}</p>
        </Link>

        <nav className="space-y-2 flex-1">
          {user?.role === 'patient' && (
            <button
              onClick={() => navigate('/my-card')}
              className="w-full flex items-center space-x-4 px-5 py-4 rounded-xl transition-all duration-300 hover:bg-[#E6F4FA] hover:shadow-[0_4px_20px_rgba(42,159,203,0.1)] group"
              style={{color: '#1A1A1A'}}
              data-testid="tab-card"
            >
              <CreditCard className="w-5 h-5 text-[#2A9FCB] group-hover:scale-110 transition-transform" />
              <span style={{fontFamily: "'Manrope', sans-serif"}}>Tessera Sanitaria</span>
            </button>
          )}
          <button
            onClick={() => navigate('/tests')}
            className="w-full flex items-center space-x-4 px-5 py-4 rounded-xl transition-all duration-300 hover:bg-[#E6F4FA] hover:shadow-[0_4px_20px_rgba(42,159,203,0.1)] group"
            style={{color: '#1A1A1A'}}
            data-testid="tab-tests"
          >
            <TestTube className="w-5 h-5 text-[#2A9FCB] group-hover:scale-110 transition-transform" />
            <span style={{fontFamily: "'Manrope', sans-serif"}}>Test Visivi</span>
          </button>
        </nav>

        {/* Google Calendar Sync Button - Luxury */}
        <div className="mt-auto mb-6 p-5 rounded-2xl bg-gradient-to-br from-[#E6F4FA] to-white border border-[#2A9FCB]/10">
          <button
            onClick={syncGoogleCalendar}
            className="w-full flex items-center justify-center gap-2 text-sm font-medium py-3 rounded-full bg-white shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_rgba(42,159,203,0.15)] transition-all duration-300 hover:-translate-y-0.5"
            style={{color: '#2A9FCB', fontFamily: "'Manrope', sans-serif"}}
            data-testid="sync-calendar-btn"
          >
            <Calendar className="w-4 h-4" />
            Sincronizza Calendario
          </button>
        </div>

        <button onClick={handleLogout} className="w-full flex items-center space-x-4 px-5 py-4 rounded-xl hover:bg-red-50 transition-all duration-300" style={{color: '#EF4444'}} data-testid="logout-btn">
          <LogOut className="w-5 h-5" />
          <span style={{fontFamily: "'Manrope', sans-serif"}}>Esci</span>
        </button>
      </div>

      {/* Main Content - Luxury Style */}
      <div className="flex-1 p-6 lg:p-10 overflow-y-auto" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
        <div className="max-w-5xl mx-auto">
          {/* Breadcrumb Navigation */}
          <Breadcrumb 
            items={[
              { label: 'Home', path: '/' },
              { label: 'La Mia Dashboard', path: '/patient' }
            ]}
            currentPage={
              activeTab === 'profile' ? 'Profilo' :
              activeTab === 'appointments' ? 'Appuntamenti' :
              activeTab === 'telemedicine' ? 'Videoconsulto' :
              activeTab === 'history' ? 'Storico Visite' :
              activeTab === 'anamnesi' ? 'Anamnesi' :
              activeTab === 'subscription' ? 'Abbonamento' :
              activeTab === 'family' ? 'Nucleo Familiare' :
              activeTab === 'documents' ? 'Documenti' :
              activeTab === 'payments' ? 'Pagamenti' :
              activeTab === 'privacy' ? 'Privacy & GDPR' :
              activeTab === 'settings' ? 'Impostazioni' : null
            }
          />
          
          {/* Profile Header - Luxury Design */}
          <div className="relative rounded-3xl shadow-[0_8px_40px_rgba(44,70,103,0.2)] mb-8">
            {/* Background - Gradient with secondary color */}
            <div className="absolute inset-0" style={{background: 'linear-gradient(135deg, #2c4667 0%, #3d5a80 100%)'}} />
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-80 h-80 rounded-full opacity-10 transform translate-x-24 -translate-y-24" style={{background: 'white'}} />
            <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-10 transform -translate-x-20 translate-y-20" style={{background: '#2A9FCB'}} />
            
            <div className="relative p-8 md:p-10">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                {/* Avatar with Photo Upload */}
                <div className="relative">
                  <div className="ring-4 ring-white/20 rounded-full shadow-xl">
                    <ProfilePhotoUploader
                      currentPhoto={user?.profile_photo}
                      onPhotoChange={(photo) => setUser(prev => ({ ...prev, profile_photo: photo }))}
                      size="lg"
                      editable={true}
                    />
                  </div>
                  {/* Online Indicator */}
                  <div className="absolute bottom-3 right-3 w-5 h-5 bg-emerald-400 rounded-full border-[3px] border-white shadow-lg" />
                </div>
                
                {/* Info */}
                <div className="flex-1 text-center md:text-left">
                  <h1 className="text-3xl md:text-4xl text-white mb-2 tracking-tight" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
                    {user?.first_name} {user?.last_name}
                  </h1>
                  <p className="text-white/60 text-sm mb-6" style={{fontFamily: "'Manrope', sans-serif"}}>{user?.email}</p>
                  
                  {/* Stats Cards - Solo per pazienti */}
                  {user?.role === 'patient' && (
                  <div className="flex justify-center md:justify-start gap-4 mb-6">
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl px-6 py-4 text-center border border-white/10 hover:bg-white/15 transition-all duration-300">
                      <p className="text-3xl font-light text-white" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>{appointments.length}</p>
                      <p className="text-xs text-white/60 tracking-wider uppercase mt-1" style={{fontFamily: "'Manrope', sans-serif"}}>Visite Totali</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl px-6 py-4 text-center border border-white/10 hover:bg-white/15 transition-all duration-300">
                      <p className="text-3xl font-light text-white" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>{concludedVisits.length}</p>
                      <p className="text-xs text-white/60 tracking-wider uppercase mt-1" style={{fontFamily: "'Manrope', sans-serif"}}>Completate</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl px-6 py-4 text-center border border-white/10 hover:bg-white/15 transition-all duration-300">
                      <p className="text-3xl font-light text-white" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>{getAllTags().length}</p>
                      <p className="text-xs text-white/60 tracking-wider uppercase mt-1" style={{fontFamily: "'Manrope', sans-serif"}}>Diagnosi</p>
                    </div>
                  </div>
                  )}

                  {/* Diagnosis Tags - Solo per pazienti */}
                  {user?.role === 'patient' && diagnosisTags.length > 0 && (
                    <div className="flex flex-wrap justify-center md:justify-start gap-2">
                      {diagnosisTags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="px-4 py-1.5 rounded-full text-xs bg-white/15 text-white border border-white/20 backdrop-blur-sm" style={{fontFamily: "'Manrope', sans-serif"}}>
                          {tag}
                        </span>
                      ))}
                      {diagnosisTags.length > 3 && (
                        <span className="px-4 py-1.5 rounded-full text-xs bg-white/15 text-white border border-white/20 backdrop-blur-sm" style={{fontFamily: "'Manrope', sans-serif"}}>
                          +{diagnosisTags.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <div className="p-2 rounded-xl bg-white/20 backdrop-blur-md border border-white/20">
                    <NotificationBell userId={user?.id} iconColor="white" />
                  </div>
                  <div className="p-2 rounded-xl bg-white/20 backdrop-blur-md border border-white/20">
                    <MessageBell userId={user?.id} iconColor="white" currentUser={user} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs - Luxury Design */}
          <div className="bg-white rounded-t-3xl shadow-[0_-4px_20px_rgba(0,0,0,0.03)] border-b border-gray-100 overflow-x-auto">
            <div className="flex min-w-max">
              {[
                ...(user?.role === 'patient' ? [
                  { id: 'appointments', icon: Calendar, label: 'Visite', color: '#2A9FCB' },
                  { id: 'telemedicine', icon: Video, label: 'Videoconsulto', color: '#10B981' },
                  { id: 'anamnesis', icon: Heart, label: 'Anamnesi', color: '#EC4899' },
                  { id: 'records', icon: FileText, label: 'Cartelle', color: '#8B5CF6' },
                  { id: 'protocols', icon: Shield, label: 'Protocolli', color: '#10B981' },
                  { id: 'family', icon: Users, label: 'Famiglia', color: '#F59E0B' },
                  { id: 'documents', icon: Upload, label: 'Documenti', color: '#10B981' },
                  { id: 'payments', icon: Receipt, label: 'Pagamenti', color: '#6366F1' },
                ] : []),
                { id: 'profile', icon: User, label: 'Profilo', color: '#2A9FCB' },
                { id: 'privacy', icon: Lock, label: 'Sicurezza e Privacy', color: '#EF4444' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-5 px-4 border-b-2 transition-all duration-300 min-w-[110px] ${
                    activeTab === tab.id 
                      ? 'bg-gradient-to-t from-[#E6F4FA]/50 to-transparent' 
                      : 'border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                  }`}
                  style={{
                    color: activeTab === tab.id ? tab.color : undefined,
                    borderBottomColor: activeTab === tab.id ? tab.color : 'transparent',
                    fontFamily: "'Manrope', sans-serif"
                  }}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="hidden sm:inline text-sm font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content - Luxury */}
          <div className="bg-white rounded-b-3xl shadow-[0_8px_40px_rgba(0,0,0,0.04)] p-8">

          {/* Appointments Tab - Luxury Style */}
          {activeTab === 'appointments' && (
            <div className="space-y-8">
              {/* Slot Recovery Messages - Show available slots */}
              <SlotRecoveryMessages userId={user?.id} onSlotClaimed={() => window.location.reload()} />

              <div className="flex justify-between items-center">
                <h2 className="text-2xl md:text-3xl tracking-tight" style={{color: '#1A1A1A', fontFamily: "'Playfair Display', Georgia, serif"}}>Le Tue Visite</h2>
                <div className="flex gap-2">
                  <button 
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const response = await axios.get(`${API}/appointments/export/calendar`, {
                          headers: { Authorization: `Bearer ${token}` },
                          responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `primevision_appuntamenti.ics`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success('Calendario esportato! Importalo nel tuo Google Calendar o Apple Calendar');
                      } catch (error) {
                        toast.error('Errore esportazione calendario');
                      }
                    }}
                    className="inline-flex items-center justify-center border-2 border-[#2A9FCB] text-[#2A9FCB] hover:bg-[#2A9FCB] hover:text-white rounded-full px-4 py-2.5 text-sm font-medium transition-all duration-300"
                    style={{fontFamily: "'Manrope', sans-serif"}}
                    title="Esporta tutti gli appuntamenti nel tuo calendario"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Esporta Calendario
                  </button>
                  <button 
                    onClick={() => navigate('/booking')} 
                    className="inline-flex items-center justify-center bg-[#2A9FCB] text-white hover:bg-[#1C729E] rounded-full px-6 py-3 text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                    style={{fontFamily: "'Manrope', sans-serif"}}
                    data-testid="book-appointment-btn"
                  >
                    Prenota Visita
                  </button>
                </div>
              </div>

              {/* Pending Confirmations Alert - Luxury */}
              {pendingConfirmations.length > 0 && (
                <div className="rounded-2xl p-8 shadow-[0_4px_20px_rgba(245,158,11,0.15)] border border-amber-200" style={{background: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)'}}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <Bell className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="text-xl text-amber-900" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>Conferma Richiesta</h3>
                      <p className="text-sm text-amber-700" style={{fontFamily: "'Manrope', sans-serif"}}>{pendingConfirmations.length} appuntament{pendingConfirmations.length > 1 ? 'i' : 'o'} da confermare</p>
                    </div>
                  </div>
                  <p className="text-sm text-amber-700 mb-6" style={{fontFamily: "'Manrope', sans-serif"}}>
                    Questi appuntamenti richiedono la tua conferma. Il medico e l&apos;amministrazione saranno notificati.
                  </p>
                  <div className="space-y-4">
                    {pendingConfirmations.map(appt => (
                      <div key={appt.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-5 bg-white rounded-xl shadow-sm border border-amber-100" data-testid={`pending-${appt.id}`}>
                        <div>
                          <p className="font-medium text-[#1A1A1A]" style={{fontFamily: "'Manrope', sans-serif"}}>{appt.reason}</p>
                          <p className="text-sm text-gray-500" style={{fontFamily: "'Manrope', sans-serif"}}>
                            {new Date(appt.appointment_date).toLocaleString('it-IT')}
                            {appt.urgent && <span className="ml-2 text-red-600 font-semibold">(Meno di 24h!)</span>}
                          </p>
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => confirmAppointment(appt.id, true)}
                            className="px-5 py-2.5 rounded-full text-sm font-medium text-white flex items-center gap-2 transition-all duration-300 hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                            style={{background: '#10B981', fontFamily: "'Manrope', sans-serif"}}
                            data-testid={`confirm-${appt.id}`}
                          >
                            <Check className="w-4 h-4" />
                            Confermo
                          </button>
                          <button
                            onClick={() => confirmAppointment(appt.id, false)}
                            className="px-5 py-2.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-300"
                            style={{fontFamily: "'Manrope', sans-serif"}}
                            data-testid={`decline-${appt.id}`}
                          >
                            Non posso
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Scheduled - Luxury */}
              <div className="rounded-2xl p-8 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-gray-100">
                <h3 className="text-xl mb-6 flex items-center gap-3" style={{color: '#1A1A1A', fontFamily: "'Playfair Display', Georgia, serif"}}>
                  <div className="w-10 h-10 rounded-full bg-[#E6F4FA] flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-[#2A9FCB]" />
                  </div>
                  Programmate <span className="text-sm font-normal text-gray-400">({scheduledAppts.length})</span>
                </h3>
                {scheduledAppts.length === 0 ? (
                  <p className="text-sm text-gray-500" style={{fontFamily: "'Manrope', sans-serif"}}>Nessuna visita programmata</p>
                ) : (
                  <div className="space-y-4">
                    {scheduledAppts.map(appt => (
                      <div key={appt.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-5 bg-gradient-to-br from-[#e8f4fd] via-[#f4f9fe] to-[#dcedf8] rounded-xl hover:shadow-md transition-all duration-300 border border-transparent hover:border-[#2A9FCB]/20" data-testid={`appointment-${appt.id}`}>
                        <div className="flex-1">
                          <p className="font-medium text-[#1A1A1A]" style={{fontFamily: "'Manrope', sans-serif"}}>{appt.reason}</p>
                          <p className="text-sm text-gray-500 mt-1" style={{fontFamily: "'Manrope', sans-serif"}}>{new Date(appt.appointment_date).toLocaleString('it-IT')}</p>
                          {appt.patient_confirmed && (
                            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 mt-2 bg-emerald-50 px-2 py-1 rounded-full">
                              <Check className="w-3 h-3" /> Confermato
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => openRescheduleModal(appt)}
                            className="p-2.5 rounded-xl hover:bg-[#E6F4FA] transition-all duration-300"
                            title="Modifica data/orario"
                            data-testid={`reschedule-${appt.id}`}
                          >
                            <Edit3 className="w-5 h-5 text-[#2A9FCB]" />
                          </button>
                          <button
                            onClick={() => cancelAppointment(appt.id)}
                            className="p-2.5 rounded-xl hover:bg-red-50 transition-all duration-300"
                            title="Cancella appuntamento"
                            data-testid={`cancel-${appt.id}`}
                          >
                            <X className="w-5 h-5 text-red-500" />
                          </button>
                          <span className="px-4 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700" style={{fontFamily: "'Manrope', sans-serif"}}>
                            Programmata
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Completed - Luxury */}
              <div className="rounded-2xl p-8 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-gray-100">
                <h3 className="text-xl mb-6 flex items-center gap-3" style={{color: '#1A1A1A', fontFamily: "'Playfair Display', Georgia, serif"}}>
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  Completate <span className="text-sm font-normal text-gray-400">({completedAppts.length})</span>
                </h3>
                {completedAppts.length === 0 ? (
                  <p className="text-sm text-gray-500" style={{fontFamily: "'Manrope', sans-serif"}}>Nessuna visita completata</p>
                ) : (
                  <div className="space-y-4">
                    {completedAppts.map(appt => (
                      <div key={appt.id} className="flex justify-between items-center p-5 bg-gradient-to-br from-[#e8f4fd] via-[#f4f9fe] to-[#dcedf8] rounded-xl border border-transparent hover:border-emerald-200 transition-all duration-300">
                        <div>
                          <p className="font-medium text-[#1A1A1A]" style={{fontFamily: "'Manrope', sans-serif"}}>{appt.reason}</p>
                          <p className="text-sm text-gray-500 mt-1" style={{fontFamily: "'Manrope', sans-serif"}}>{new Date(appt.appointment_date).toLocaleString('it-IT')}</p>
                        </div>
                        <span className="px-4 py-1.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700" style={{fontFamily: "'Manrope', sans-serif"}}>
                          Completata
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Missed - Luxury */}
              {missedAppts.length > 0 && (
                <div className="rounded-2xl p-8 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-gray-100">
                  <h3 className="text-xl mb-6 flex items-center gap-3" style={{color: '#1A1A1A', fontFamily: "'Playfair Display', Georgia, serif"}}>
                    <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                    Saltate <span className="text-sm font-normal text-gray-400">({missedAppts.length})</span>
                  </h3>
                  <div className="space-y-4">
                    {missedAppts.map(appt => (
                      <div key={appt.id} className="flex justify-between items-center p-5 bg-gradient-to-br from-[#e8f4fd] via-[#f4f9fe] to-[#dcedf8] rounded-xl border border-transparent hover:border-red-200 transition-all duration-300">
                        <div>
                          <p className="font-medium text-[#1A1A1A]" style={{fontFamily: "'Manrope', sans-serif"}}>{appt.reason}</p>
                          <p className="text-sm text-gray-500 mt-1" style={{fontFamily: "'Manrope', sans-serif"}}>{new Date(appt.appointment_date).toLocaleString('it-IT')}</p>
                        </div>
                        <span className="px-4 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-700" style={{fontFamily: "'Manrope', sans-serif"}}>
                          Saltata
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Telemedicina Tab */}
          {activeTab === 'telemedicine' && (
            <VideoConsultManager isDoctor={false} currentUser={user} />
          )}

          {/* Anamnesis Tab - Luxury Style */}
          {activeTab === 'anamnesis' && (
            <div className="space-y-8">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl md:text-3xl tracking-tight" style={{color: '#1A1A1A', fontFamily: "'Playfair Display', Georgia, serif"}}>La Tua Anamnesi</h2>
                <div className="flex gap-3">
                  <button 
                    onClick={() => setShowGuidedAnamnesis(true)}
                    className="flex items-center gap-2 px-6 py-3 rounded-full font-medium text-white transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                    style={{background: '#2A9FCB', fontFamily: "'Manrope', sans-serif"}}
                    data-testid="open-guided-anamnesis-btn"
                  >
                    <ClipboardList className="w-4 h-4" />
                    Compila Anamnesi Guidata
                  </button>
                </div>
              </div>

              {/* Guided Anamnesis Modal */}
              {showGuidedAnamnesis && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <GuidedAnamnesis 
                    onComplete={() => {
                      setShowGuidedAnamnesis(false);
                      loadData(); // Reload to show new report
                    }}
                    onCancel={() => setShowGuidedAnamnesis(false)}
                  />
                </div>
              )}

              {/* Saved Anamnesis Reports from Chatbot/Guided Form */}
              {savedAnamnesisReports.length > 0 && (
                <div className="rounded-xl p-6 shadow-sm" style={{background: 'linear-gradient(135deg, #e8f4fd 0%, #CCFBF1 100%)', border: '1px solid #2A9FCB'}}>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{color: '#2A9FCB'}}>
                    <ClipboardList className="w-5 h-5" />
                    Anamnesi Salvate
                    <span className="ml-2 px-2 py-0.5 bg-[#2A9FCB] text-white text-xs rounded-full">
                      {savedAnamnesisReports.length}
                    </span>
                  </h3>
                  <p className="text-sm mb-4" style={{color: '#6B7280'}}>
                    Queste informazioni vengono condivise con il medico durante la visita.
                  </p>
                  <div className="space-y-3">
                    {savedAnamnesisReports.map((report, idx) => (
                      <div 
                        key={report.id || idx} 
                        className="bg-white rounded-lg overflow-hidden"
                        style={{border: '1px solid #E5E7EB'}}
                      >
                        <button
                          onClick={() => setExpandedReportId(expandedReportId === report.id ? null : report.id)}
                          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
                              <FileText className="w-5 h-5" style={{color: '#2A9FCB'}} />
                            </div>
                            <div className="text-left">
                              <p className="font-medium" style={{color: '#2A9FCB'}}>
                                Anamnesi del {new Date(report.created_at).toLocaleDateString('it-IT')}
                              </p>
                              <p className="text-xs text-gray-500">
                                {report.critical_tags?.length > 0 
                                  ? `${report.critical_tags.length} segnalazioni importanti`
                                  : 'Nessuna segnalazione critica'
                                }
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {report.critical_tags?.length > 0 && (
                              <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                                Attenzione
                              </span>
                            )}
                            {expandedReportId === report.id ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </button>
                        
                        {expandedReportId === report.id && (
                          <div className="px-4 pb-4 border-t" style={{borderColor: '#E5E7EB'}}>
                            {/* Critical Tags */}
                            {report.critical_tags?.length > 0 && (
                              <div className="flex flex-wrap gap-2 py-3">
                                {report.critical_tags.map((tag, i) => (
                                  <span key={i} className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded-full font-medium">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* Report Content */}
                            <div className="p-4 rounded-lg text-sm whitespace-pre-wrap" style={{background: '#F9FAFB', color: '#374151'}}>
                              {report.report_text}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Diagnosis Tags Display */}
              {diagnosisTags.length > 0 && (
                <div className="rounded-xl p-6 shadow-sm" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{color: '#2A9FCB'}}>
                    <Tag className="w-5 h-5" />
                    Diagnosi Attuali
                  </h3>
                  <p className="text-sm mb-3" style={{color: '#6B7280'}}>Le diagnosi vengono inserite dal medico durante le visite.</p>
                  <div className="flex flex-wrap gap-2">
                    {diagnosisTags.map((tag, idx) => (
                      <span key={idx} className="px-4 py-2 rounded-full  text-sm font-medium" style={{background: '#2A9FCB', color: 'white'}} data-testid={`anamnesis-diagnosis-tag-${idx}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Medical Records Tab */}
          {activeTab === 'records' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold" style={{color: '#2A9FCB'}}>Le Tue Cartelle Cliniche</h2>
              </div>
              
              {/* Concluded Visits - Main content */}
              {concludedVisits.length > 0 ? (
                <div className="space-y-4">
                  {concludedVisits.map((visit, idx) => (
                    <div key={visit.id || idx} className="rounded-xl shadow-sm bg-white overflow-hidden">
                      {/* Header */}
                      <div className="p-4 flex justify-between items-center border-b" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
                        <div>
                          <h4 className="text-lg font-semibold" style={{color: '#2A9FCB'}}>
                            Visita del {new Date(visit.concluded_at).toLocaleDateString('it-IT')}
                          </h4>
                          <p className="text-sm" style={{color: '#6B7280'}}>{visit.doctor_name}</p>
                        </div>
                        <span className="text-xs px-3 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                          Conclusa
                        </span>
                      </div>
                      
                      {/* Summary */}
                      <div className="p-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="p-3 rounded-lg" style={{background: '#F0FAF5'}}>
                            <h5 className="font-semibold text-sm mb-1" style={{color: '#2A9FCB'}}>Diagnosi</h5>
                            <p className="text-sm line-clamp-2">{visit.diagnosi || 'Non specificata'}</p>
                          </div>
                          <div className="p-3 rounded-lg" style={{background: '#FFF8E7'}}>
                            <h5 className="font-semibold text-sm mb-1" style={{color: '#D97706'}}>Terapia</h5>
                            <p className="text-sm line-clamp-2">{visit.terapia || 'Non specificata'}</p>
                          </div>
                        </div>
                        
                        {visit.tags_added && visit.tags_added.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {visit.tags_added.map((tag, i) => (
                              <span key={i} className="px-2 py-0.5 text-xs rounded-full font-medium" style={{background: '#2A9FCB', color: 'white'}}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="p-4 border-t flex flex-wrap gap-3">
                        <button
                          onClick={() => {
                            const token = localStorage.getItem('token');
                            const url = `${process.env.REACT_APP_BACKEND_URL}/api/referto/${visit.id}/pdf?token=${token}`;
                            window.open(url, '_blank');
                          }}
                          className="flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                          style={{background: '#2A9FCB', color: 'white'}}
                          data-testid={`view-pdf-btn-${visit.id}`}
                        >
                          <Eye className="w-4 h-4" />
                          Anteprima PDF
                        </button>
                        <button
                          onClick={() => {
                            const token = localStorage.getItem('token');
                            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referto/${visit.id}/pdf?token=${token}`, {
                              headers: { Authorization: `Bearer ${token}` }
                            })
                              .then(response => {
                                if (!response.ok) throw new Error('Errore nel download');
                                return response.blob();
                              })
                              .then(blob => {
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `referto_primevision_${visit.id.substring(0,8)}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                                toast.success('PDF scaricato con successo');
                              })
                              .catch(err => {
                                console.error('Download error:', err);
                                toast.error('Errore nel download del PDF');
                              });
                          }}
                          className="flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 border"
                          style={{borderColor: '#2A9FCB', color: '#2A9FCB'}}
                          data-testid={`download-pdf-btn-${visit.id}`}
                        >
                          <Download className="w-4 h-4" />
                          Scarica PDF
                        </button>
                        <button
                          onClick={() => {
                            const token = localStorage.getItem('token');
                            const url = `${process.env.REACT_APP_BACKEND_URL}/api/referto/${visit.id}/pdf?token=${token}`;
                            const printWin = window.open(url, '_blank');
                            if (printWin) {
                              printWin.onload = () => printWin.print();
                            }
                          }}
                          className="flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 border"
                          style={{borderColor: '#6B7280', color: '#6B7280'}}
                          data-testid={`print-pdf-btn-${visit.id}`}
                        >
                          <Printer className="w-4 h-4" />
                          Stampa
                        </button>
                        <button
                          onClick={() => {
                            const visitDate = visit.concluded_at ? new Date(visit.concluded_at).toLocaleDateString('it-IT') : '';
                            const phonePrefill = (patientRecord?.phone || editForm?.phone || '').toString();
                            setShareSmsModal({ show: true, visitId: visit.id, visitDate, phone: phonePrefill, recipientName: '', sending: false });
                          }}
                          className="flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                          style={{background: '#10B981', color: 'white'}}
                          data-testid={`send-sms-pdf-btn-${visit.id}`}
                        >
                          <Send className="w-4 h-4" />
                          Invia via SMS
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl p-8 shadow-sm text-center" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
                  <FileText className="w-12 h-12 mx-auto mb-4" style={{color: '#2A9FCB', opacity: 0.5}} />
                  <p className="font-medium mb-2" style={{color: '#2A9FCB'}}>Nessuna cartella clinica disponibile</p>
                  <p className="text-sm" style={{color: '#9CA3AF'}}>
                    Le cartelle cliniche verranno visualizzate qui dopo le visite con il medico.
                  </p>
                </div>
              )}
              
              {/* Legacy medical records */}
              {medicalRecords.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4" style={{color: '#2A9FCB'}}>Archivio Storico</h3>
                  <div className="space-y-3">
                    {medicalRecords.map(record => (
                      <div key={record.id} className="rounded-xl p-4 shadow-sm bg-white" data-testid={`record-${record.id}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold" style={{color: '#2A9FCB'}}>{record.diagnosis}</h4>
                            <p className="text-sm mt-1"><strong>Trattamento:</strong> {record.treatment}</p>
                          </div>
                          <span className="text-xs" style={{color: '#9CA3AF'}}>{new Date(record.created_at).toLocaleDateString('it-IT')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tests Tab */}
          {activeTab === 'tests' && (
            <div>
              <h2 className="text-2xl font-semibold mb-6" style={{color: '#2A9FCB'}}>Risultati Test</h2>
              {testResults.length === 0 ? (
                <div className="rounded-xl p-6 shadow-sm" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
                  <p className="text-sm mb-4" style={{color: '#9CA3AF'}}>Nessun test effettuato</p>
                  <button onClick={() => navigate('/tests')} className="btn-primary" data-testid="go-to-tests-btn">
                    Vai ai Test
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {testResults.map(test => (
                    <div key={test.id} className="rounded-xl p-6 shadow-sm" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}} data-testid={`test-${test.id}`}>
                      <h3 className="text-lg font-semibold mb-2" style={{color: '#2A9FCB'}}>{test.test_name}</h3>
                      <p className="text-sm mb-4" style={{color: '#9CA3AF'}}>{new Date(test.created_at).toLocaleDateString('it-IT')}</p>
                      
                      {/* Formatted Test Results */}
                      <div className="space-y-3">
                        {test.result_data && (
                          <>
                            {/* Score/Result */}
                            {test.result_data.score !== undefined && (
                              <div className="flex items-center justify-between p-3 rounded-lg bg-white">
                                <span className="font-medium" style={{color: '#6B7280'}}>Punteggio</span>
                                <span className="text-xl font-bold" style={{color: test.result_data.score >= 80 ? '#22C55E' : test.result_data.score >= 50 ? '#F59E0B' : '#EF4444'}}>
                                  {test.result_data.score}%
                                </span>
                              </div>
                            )}
                            
                            {/* Correct/Total */}
                            {test.result_data.correct !== undefined && (
                              <div className="flex items-center justify-between p-3 rounded-lg bg-white">
                                <span className="font-medium" style={{color: '#6B7280'}}>Risposte Corrette</span>
                                <span className="font-bold" style={{color: '#2A9FCB'}}>
                                  {test.result_data.correct} / {test.result_data.total || test.result_data.correct + (test.result_data.incorrect || 0)}
                                </span>
                              </div>
                            )}
                            
                            {/* Result Status */}
                            {test.result_data.result && (
                              <div className={`p-3 rounded-lg text-center ${
                                test.result_data.result === 'pass' || test.result_data.result === 'normal' 
                                  ? 'bg-green-100 text-green-800' 
                                  : test.result_data.result === 'warning' 
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                              }`}>
                                <span className="font-semibold">
                                  {test.result_data.result === 'pass' || test.result_data.result === 'normal' 
                                    ? '✓ Visione Normale' 
                                    : test.result_data.result === 'warning'
                                      ? '⚠️ Possibile Anomalia'
                                      : '⚠️ Consulta uno Specialista'}
                                </span>
                              </div>
                            )}
                            
                            {/* Analysis/Description */}
                            {test.result_data.analysis && (
                              <div className="p-3 rounded-lg bg-white">
                                <span className="text-sm font-medium block mb-1" style={{color: '#6B7280'}}>Analisi</span>
                                <p className="text-sm" style={{color: '#2A9FCB'}}>{test.result_data.analysis}</p>
                              </div>
                            )}
                            
                            {/* Detailed responses for Ishihara */}
                            {test.result_data.responses && Array.isArray(test.result_data.responses) && (
                              <div className="p-3 rounded-lg bg-white">
                                <span className="text-sm font-medium block mb-2" style={{color: '#6B7280'}}>Dettaglio Risposte</span>
                                <div className="grid grid-cols-3 gap-2">
                                  {test.result_data.responses.map((resp, idx) => (
                                    <div key={idx} className={`text-center p-2 rounded ${resp.correct ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                      <span className="text-xs">Tav. {idx + 1}</span>
                                      <div className="font-bold">{resp.correct ? '✓' : '✗'}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div>
              <h2 className="text-2xl font-semibold mb-6" style={{color: '#2A9FCB'}}>Timeline Clinica</h2>
              
              {timeline.length > 0 ? (
                <div className="space-y-4">
                  {timeline.map((event, idx) => (
                    <div 
                      key={event.id || idx} 
                      className="p-4 rounded-xl transition-shadow hover:shadow-md"
                      style={{background: '#F9FAFB', border: '1px solid #E5E7EB'}}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          event.type === 'appointment' ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          {event.type === 'appointment' ? (
                            <Calendar className="w-5 h-5 text-green-600" />
                          ) : (
                            <TestTube className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-[#2A9FCB]">{event.title}</h4>
                          <p className="text-sm text-[#2A9FCB]/70">{event.description}</p>
                          <p className="text-xs text-[#2A9FCB]/60 mt-1">
                            {formatDate(event.date)}
                          </p>
                          {event.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {event.tags.map((tag, i) => (
                                <span 
                                  key={i} 
                                  className={`px-2 py-0.5 rounded-full text-xs ${getTagColor(tag)}`}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Activity className="w-12 h-12 mx-auto text-[#2A9FCB]/50 mb-4" />
                  <p className="text-[#2A9FCB]/70">Nessuna visita registrata</p>
                </div>
              )}
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold" style={{color: '#2A9FCB'}}>La Tua Recensione</h2>
              <p className="text-[#2A9FCB]/70">
                Condividi la tua esperienza con PRIME VISION. Seleziona da 1 a 4 stelle e, se lo desideri, aggiungi un commento. 
                L'amministratore esaminerà la tua recensione prima di pubblicarla.
              </p>
              <div className="max-w-lg">
                <ReviewForm userRole="patient" />
              </div>
            </div>
          )}

          {/* Family Tab */}
          {activeTab === 'family' && (
            <FamilyManager patientId={user?.id} />
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <DocumentManager patientId={user?.id} />
          )}

          {/* Protocols Tab - Protocolli di Prevenzione */}
          {activeTab === 'protocols' && (
            <PatientProtocolsView patientId={user?.id} />
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <PaymentManager patientId={user?.id} />
          )}

          {/* Privacy & GDPR Tab */}
          {activeTab === 'privacy' && (
            <GDPRManager userId={user?.id} />
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <PatientMessagesTab userId={user?.id} />
          )}

          {/* Profile Tab - Anagrafica & Sicurezza */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Header con pulsante Modifica */}
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold" style={{color: '#2A9FCB'}}>Profilo & Sicurezza</h2>
                {!isEditing ? (
                  <button 
                    onClick={startEditing}
                    className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                    style={{background: 'rgba(127, 179, 160, 0.1)', color: '#2A9FCB'}}
                    data-testid="edit-profile-btn"
                  >
                    <Edit2 className="w-4 h-4" />
                    Modifica Dati
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button 
                      onClick={cancelEditing}
                      className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 bg-gray-100 text-gray-600"
                    >
                      <X className="w-4 h-4" />
                      Annulla
                    </button>
                    <button 
                      onClick={saveProfile}
                      disabled={saving}
                      className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 text-white"
                      style={{background: '#2A9FCB'}}
                      data-testid="save-profile-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Salva
                    </button>
                  </div>
                )}
              </div>

              {/* Dati Anagrafici */}
              <div className="rounded-xl p-6" style={{background: '#F9FAFB', border: '1px solid #E5E7EB'}}>
                <h3 className="text-lg font-bold mb-4" style={{color: '#2A9FCB'}}>Dati Anagrafici</h3>
                
                {!isEditing ? (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-white">
                      <p className="text-xs text-[#2A9FCB]/70 mb-1">Nome</p>
                      <p className="font-medium text-[#2A9FCB]">{user?.first_name || 'Non specificato'}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white">
                      <p className="text-xs text-[#2A9FCB]/70 mb-1">Cognome</p>
                      <p className="font-medium text-[#2A9FCB]">{user?.last_name || 'Non specificato'}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white">
                      <p className="text-xs text-[#2A9FCB]/70 mb-1">Email</p>
                      <p className="font-medium text-[#2A9FCB]">{user?.email}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white">
                      <p className="text-xs text-[#2A9FCB]/70 mb-1">Telefono</p>
                      <p className="font-medium text-[#2A9FCB]">{user?.phone || 'Non specificato'}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white">
                      <p className="text-xs text-[#2A9FCB]/70 mb-1">Codice Fiscale</p>
                      <p className="font-medium text-[#2A9FCB]">{user?.codice_fiscale || 'Non specificato'}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white">
                      <p className="text-xs text-[#2A9FCB]/70 mb-1">Data di Nascita</p>
                      <p className="font-medium text-[#2A9FCB]">{user?.date_of_birth || 'Non specificato'}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white">
                      <p className="text-xs text-[#2A9FCB]/70 mb-1">Luogo di Nascita</p>
                      <p className="font-medium text-[#2A9FCB]">{user?.place_of_birth || 'Non specificato'}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white">
                      <p className="text-xs text-[#2A9FCB]/70 mb-1">Sesso</p>
                      <p className="font-medium text-[#2A9FCB]">{user?.gender || 'Non specificato'}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white sm:col-span-2">
                      <p className="text-xs text-[#2A9FCB]/70 mb-1">Indirizzo</p>
                      <p className="font-medium text-[#2A9FCB]">
                        {user?.address_street ? `${user.address_street} ${user.address_number || ''}, ${user.address_zip || ''} ${user.address_city || ''}` : 'Non specificato'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 mb-1 block">Nome</label>
                      <input
                        type="text"
                        value={editForm.first_name || ''}
                        onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                        className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#2A9FCB]/20"
                        style={{borderColor: '#E5E7EB'}}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 mb-1 block">Cognome</label>
                      <input
                        type="text"
                        value={editForm.last_name || ''}
                        onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                        className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#2A9FCB]/20"
                        style={{borderColor: '#E5E7EB'}}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 mb-1 block">Telefono</label>
                      <input
                        type="tel"
                        value={editForm.phone || ''}
                        onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                        className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#2A9FCB]/20"
                        style={{borderColor: '#E5E7EB'}}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 mb-1 block">Codice Fiscale</label>
                      <input
                        type="text"
                        value={editForm.codice_fiscale || ''}
                        onChange={(e) => setEditForm({...editForm, codice_fiscale: e.target.value.toUpperCase()})}
                        className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#2A9FCB]/20"
                        style={{borderColor: '#E5E7EB'}}
                        maxLength={16}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 mb-1 block">Data di Nascita</label>
                      <input
                        type="date"
                        value={editForm.date_of_birth || ''}
                        onChange={(e) => setEditForm({...editForm, date_of_birth: e.target.value})}
                        className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#2A9FCB]/20"
                        style={{borderColor: '#E5E7EB'}}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 mb-1 block">Luogo di Nascita</label>
                      <input
                        type="text"
                        value={editForm.place_of_birth || ''}
                        onChange={(e) => setEditForm({...editForm, place_of_birth: e.target.value})}
                        className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#2A9FCB]/20"
                        style={{borderColor: '#E5E7EB'}}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 mb-1 block">Sesso</label>
                      <select
                        value={editForm.gender || ''}
                        onChange={(e) => setEditForm({...editForm, gender: e.target.value})}
                        className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#2A9FCB]/20"
                        style={{borderColor: '#E5E7EB'}}
                      >
                        <option value="">Seleziona</option>
                        <option value="M">Maschio</option>
                        <option value="F">Femmina</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70 mb-1 block">Via</label>
                      <input
                        type="text"
                        value={editForm.address_street || ''}
                        onChange={(e) => setEditForm({...editForm, address_street: e.target.value})}
                        className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#2A9FCB]/20"
                        style={{borderColor: '#E5E7EB'}}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-xs text-[#2A9FCB]/70 mb-1 block">N°</label>
                        <input
                          type="text"
                          value={editForm.address_number || ''}
                          onChange={(e) => setEditForm({...editForm, address_number: e.target.value})}
                          className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#2A9FCB]/20"
                          style={{borderColor: '#E5E7EB'}}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-[#2A9FCB]/70 mb-1 block">CAP</label>
                        <input
                          type="text"
                          value={editForm.address_zip || ''}
                          onChange={(e) => setEditForm({...editForm, address_zip: e.target.value})}
                          className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#2A9FCB]/20"
                          style={{borderColor: '#E5E7EB'}}
                          maxLength={5}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-[#2A9FCB]/70 mb-1 block">Città</label>
                        <input
                          type="text"
                          value={editForm.address_city || ''}
                          onChange={(e) => setEditForm({...editForm, address_city: e.target.value})}
                          className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-[#2A9FCB]/20"
                          style={{borderColor: '#E5E7EB'}}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Sicurezza Account */}
              <div className="rounded-xl p-6" style={{background: '#F9FAFB', border: '1px solid #E5E7EB'}}>
                <h3 className="text-lg font-bold mb-4" style={{color: '#2A9FCB'}}>Sicurezza Account</h3>
                
                {/* Diagnosis Tags (read-only) */}
                {diagnosisTags.length > 0 && (
                  <div className="mb-6 p-4 rounded-xl" style={{background: 'rgba(127, 179, 160, 0.1)', border: '1px solid #2A9FCB'}}>
                    <div className="flex items-center gap-2 mb-3">
                      <Tag className="w-5 h-5" style={{color: '#2A9FCB'}} />
                      <h4 className="font-medium" style={{color: '#2A9FCB'}}>Tags Diagnosi Mediche</h4>
                    </div>
                    <p className="text-xs text-[#2A9FCB]/70 mb-3">
                      Questi tag sono stati assegnati dal tuo medico
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {diagnosisTags.map((tag, idx) => (
                        <span 
                          key={idx} 
                          className={`px-3 py-1 rounded-full text-sm font-medium ${getTagColor(tag)}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Change Password */}
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-white" style={{border: '1px solid #E5E7EB'}}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{background: 'rgba(127, 179, 160, 0.2)'}}>
                          <KeyRound className="w-5 h-5" style={{color: '#2A9FCB'}} />
                        </div>
                        <div>
                          <h4 className="font-medium text-[#2A9FCB]">Cambia Password</h4>
                          <p className="text-xs text-[#2A9FCB]/70">Modifica la tua password di accesso</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowPasswordModal(true)}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-white"
                        style={{background: '#2A9FCB'}}
                        data-testid="change-password-btn"
                      >
                        Modifica
                      </button>
                    </div>
                  </div>

                  {/* Email verified */}
                  <div className="p-4 rounded-xl bg-white" style={{border: '1px solid #E5E7EB'}}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{background: 'rgba(127, 179, 160, 0.2)'}}>
                        <CheckCircle className="w-5 h-5" style={{color: '#2A9FCB'}} />
                      </div>
                      <div>
                        <h4 className="font-medium text-[#2A9FCB]">Email Verificata</h4>
                        <p className="text-xs text-[#2A9FCB]/70">{user?.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Phone verified */}
                  <div className="p-4 rounded-xl bg-white" style={{border: '1px solid #E5E7EB'}}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{background: 'rgba(127, 179, 160, 0.2)'}}>
                        <CheckCircle className="w-5 h-5" style={{color: '#2A9FCB'}} />
                      </div>
                      <div>
                        <h4 className="font-medium text-[#2A9FCB]">Telefono</h4>
                        <p className="text-xs text-[#2A9FCB]/70">{user?.phone || 'Non specificato'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          </div> {/* End Tab Content */}
        </div> {/* End max-w-4xl */}
      </div> {/* End Main Content flex-1 */}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold" style={{color: '#2A9FCB'}}>Cambia Password</h3>
              <button 
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordForm({ old: '', new: '', confirm: '' });
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-[#2A9FCB]">Password Attuale</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                  <input
                    type="password"
                    value={passwordForm.old}
                    onChange={(e) => setPasswordForm({...passwordForm, old: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 border rounded-lg"
                    style={{borderColor: '#E5E7EB'}}
                    placeholder="Inserisci password attuale"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-[#2A9FCB]">Nuova Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                  <input
                    type="password"
                    value={passwordForm.new}
                    onChange={(e) => setPasswordForm({...passwordForm, new: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 border rounded-lg"
                    style={{borderColor: '#E5E7EB'}}
                    placeholder="Minimo 6 caratteri"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-[#2A9FCB]">Conferma Nuova Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                  <input
                    type="password"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 border rounded-lg"
                    style={{borderColor: '#E5E7EB'}}
                    placeholder="Ripeti la nuova password"
                  />
                </div>
              </div>

              {/* Password match indicator */}
              {passwordForm.new && passwordForm.confirm && (
                <div className={`flex items-center gap-2 text-sm ${
                  passwordForm.new === passwordForm.confirm ? 'text-green-600' : 'text-red-500'
                }`}>
                  {passwordForm.new === passwordForm.confirm ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Le password corrispondono
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4" />
                      Le password non corrispondono
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordForm({ old: '', new: '', confirm: '' });
                }}
                className="flex-1 py-3 rounded-lg border font-medium"
                style={{borderColor: '#E5E7EB', color: '#2A9FCB'}}
              >
                Annulla
              </button>
              <button
                onClick={handleChangePassword}
                disabled={changingPassword || !passwordForm.old || !passwordForm.new || passwordForm.new !== passwordForm.confirm}
                className="flex-1 py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2"
                style={{
                  background: changingPassword || !passwordForm.old || !passwordForm.new || passwordForm.new !== passwordForm.confirm
                    ? '#9EC5B5'
                    : '#2A9FCB'
                }}
                data-testid="confirm-password-change-btn"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  'Cambia Password'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {rescheduleModal.open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b" style={{borderColor: '#E5E7EB'}}>
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold" style={{color: '#2A9FCB'}}>Modifica Appuntamento</h3>
                <button 
                  onClick={() => setRescheduleModal({ open: false, appointment: null })}
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5 text-[#2A9FCB]/70" />
                </button>
              </div>
              <p className="text-sm text-[#2A9FCB]/80 mt-2">
                Appuntamento attuale: {rescheduleModal.appointment && new Date(rescheduleModal.appointment.appointment_date).toLocaleString('it-IT')}
              </p>
            </div>

            <div className="p-6">
              {/* Calendar Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button onClick={prevMonth} className="p-2 rounded-full hover:bg-gray-100" style={{color: '#2A9FCB'}}>
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h4 className="font-semibold" style={{color: '#2A9FCB'}}>
                  {MONTHS[currentMonth - 1]} {currentYear}
                </h4>
                <button onClick={nextMonth} className="p-2 rounded-full hover:bg-gray-100" style={{color: '#2A9FCB'}}>
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Weekday Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {WEEKDAYS.map(day => (
                  <div key={day} className="text-center text-xs font-medium py-1 text-[#2A9FCB]/70">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 mb-6">
                {/* Calculate offset */}
                {(() => {
                  const firstDay = new Date(currentYear, currentMonth - 1, 1).getDay();
                  const offset = firstDay === 0 ? 6 : firstDay - 1;
                  return Array.from({ length: offset }).map((_, i) => (
                    <div key={`empty-${i}`} className="aspect-square" />
                  ));
                })()}
                
                {calendarData.map(day => {
                  const isAvailable = day.is_working && day.available > 0;
                  const isSelected = day.date === rescheduleDate;
                  const isPast = new Date(day.date) < new Date().setHours(0, 0, 0, 0);
                  
                  return (
                    <button
                      key={day.date}
                      onClick={() => isAvailable && !isPast && handleDateSelect(day.date)}
                      disabled={!isAvailable || isPast}
                      className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition-all
                        ${isSelected ? 'ring-2 ring-offset-1' : ''}
                        ${isAvailable && !isPast ? 'hover:scale-105 cursor-pointer' : 'opacity-40 cursor-not-allowed'}
                      `}
                      style={{
                        background: isAvailable && !isPast ? (isSelected ? '#2A9FCB' : '#D1FAE5') : '#F3F4F6',
                        color: isSelected ? '#fff' : (isAvailable && !isPast ? '#065F46' : '#9CA3AF'),
                        '--tw-ring-color': '#2A9FCB'
                      }}
                    >
                      <span className="font-medium">{day.day}</span>
                      {isAvailable && !isPast && (
                        <span className="text-[10px]">{day.available} slot</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Time Slots */}
              {rescheduleDate && (
                <div>
                  <h4 className="font-semibold mb-3" style={{color: '#2A9FCB'}}>
                    Seleziona Orario - {new Date(rescheduleDate).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </h4>
                  
                  {loadingSlots ? (
                    <div className="text-center py-4 text-[#2A9FCB]/70">Caricamento slot...</div>
                  ) : availableSlots.length === 0 ? (
                    <div className="text-center py-4 text-red-600">Nessuno slot disponibile</div>
                  ) : (
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {availableSlots.map(slot => (
                        <button
                          key={slot}
                          onClick={() => setRescheduleTime(slot)}
                          className={`py-2 px-3 rounded-lg text-sm font-medium transition-all
                            ${rescheduleTime === slot ? 'text-white' : 'bg-gray-100 hover:bg-gray-200'}
                          `}
                          style={{
                            background: rescheduleTime === slot ? '#2A9FCB' : undefined,
                            color: rescheduleTime === slot ? '#fff' : '#2A9FCB'
                          }}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t flex justify-end gap-3" style={{borderColor: '#E5E7EB'}}>
              <button
                onClick={() => setRescheduleModal({ open: false, appointment: null })}
                className="px-6 py-3 rounded-xl font-medium bg-gray-200 text-[#2A9FCB] hover:bg-gray-300"
              >
                Annulla
              </button>
              <button
                onClick={submitReschedule}
                disabled={!rescheduleDate || !rescheduleTime}
                className="px-6 py-3 rounded-xl font-medium text-white disabled:opacity-50"
                style={{background: '#2A9FCB'}}
              >
                Conferma Modifica
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Request Popup */}
      {showReviewPopup && (
        <ReviewRequestPopup
          onClose={() => setShowReviewPopup(false)}
          onSuccess={() => {
            setShowReviewPopup(false);
            setHasReviewRequests(false);
            loadData(); // Reload data
          }}
        />
      )}

      {/* Google Calendar Sync Modal */}
      {calendarSyncModal.show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCalendarSyncModal({ show: false, loading: false, syncData: null })}>
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b bg-gradient-to-r from-[#2A9FCB] to-[#2A9FCB]">
              <div className="flex items-center justify-between">
                <div className="text-white">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Calendar className="w-6 h-6" />
                    Sincronizza con Google Calendar
                  </h2>
                  <p className="text-white/80 text-sm mt-1">
                    Non perdere mai un appuntamento!
                  </p>
                </div>
                <button
                  onClick={() => setCalendarSyncModal({ show: false, loading: false, syncData: null })}
                  className="p-2 rounded-full hover:bg-white/20 transition-all text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {calendarSyncModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#2A9FCB]" />
                  <span className="ml-3 text-gray-600">Preparazione sincronizzazione...</span>
                </div>
              ) : calendarSyncModal.syncData ? (
                <div className="space-y-6">
                  {/* Quick Sync Button */}
                  <div className="text-center">
                    <button
                      onClick={openGoogleCalendarSync}
                      className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3"
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.383 0 0 5.383 0 12s5.383 12 12 12 12-5.383 12-12S18.617 0 12 0zm-1.2 17.4h2.4v-2.4h-2.4v2.4zm0-4.8h2.4V6.6h-2.4v6z"/>
                      </svg>
                      Aggiungi a Google Calendar
                    </button>
                    <p className="text-sm text-gray-500 mt-2">
                      Si aprirà Google Calendar per confermare l'aggiunta
                    </p>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-[#2A9FCB]" />
                      Il tuo calendario includerà:
                    </h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Tutte le tue visite e appuntamenti
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Promemoria automatici (24h e 2h prima)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Nome del medico e sede della visita
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Aggiornamento automatico ogni 12-24 ore
                      </li>
                    </ul>
                  </div>

                  {/* Manual URL */}
                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-700 mb-3">
                      Oppure copia l'URL manualmente:
                    </h3>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={calendarSyncModal.syncData.feed_url}
                        className="flex-1 px-3 py-2 text-sm border rounded-lg bg-gray-50 text-gray-600 truncate"
                      />
                      <button
                        onClick={copyFeedUrl}
                        className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-all"
                      >
                        Copia
                      </button>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <p className="text-sm text-green-800">
                      <strong>✨ Suggerimento:</strong> Dopo aver aggiunto il calendario, i tuoi appuntamenti appariranno automaticamente su Google Calendar, Apple Calendar o qualsiasi altra app che supporti i calendari iCal.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Modale Invia Referto via SMS */}
      {shareSmsModal.show && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="share-sms-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 flex items-center justify-between border-b" style={{background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)'}}>
              <div className="flex items-center gap-3 text-white">
                <Send className="w-5 h-5" />
                <h3 className="font-semibold text-lg">Invia referto via SMS</h3>
              </div>
              <button
                onClick={() => setShareSmsModal({ show: false, visitId: null, visitDate: '', phone: '', recipientName: '', sending: false })}
                className="text-white/80 hover:text-white"
                data-testid="share-sms-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm" style={{color: '#4B5563'}}>
                Invieremo un SMS con il link al PDF del referto della visita
                {shareSmsModal.visitDate ? ` del ${shareSmsModal.visitDate}` : ''}. Il link sara&apos; valido <strong>7 giorni</strong> e protetto da firma digitale.
              </p>
              <div>
                <label className="block text-sm font-medium mb-1" style={{color: '#374151'}}>
                  Numero di telefono <span style={{color: '#EF4444'}}>*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{color: '#9CA3AF'}} />
                  <input
                    type="tel"
                    placeholder="+39 333 1234567"
                    value={shareSmsModal.phone}
                    onChange={(e) => setShareSmsModal(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full pl-9 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    style={{borderColor: '#D1D5DB'}}
                    data-testid="share-sms-phone-input"
                  />
                </div>
                <p className="text-xs mt-1" style={{color: '#9CA3AF'}}>Includi il prefisso internazionale (es. +39)</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{color: '#374151'}}>
                  Nome destinatario (opzionale)
                </label>
                <input
                  type="text"
                  placeholder="Es. Dott. Rossi"
                  value={shareSmsModal.recipientName}
                  onChange={(e) => setShareSmsModal(prev => ({ ...prev, recipientName: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  style={{borderColor: '#D1D5DB'}}
                  data-testid="share-sms-recipient-input"
                />
                <p className="text-xs mt-1" style={{color: '#9CA3AF'}}>Personalizza il testo dell&apos;SMS (es. medico di famiglia)</p>
              </div>
              <div className="rounded-lg p-3 text-xs" style={{background: '#F0FDF4', color: '#166534'}}>
                <strong>Privacy:</strong> il link contiene una firma sicura limitata a questo solo referto. Chi non ha il link non puo&apos; accedere ai tuoi dati.
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShareSmsModal({ show: false, visitId: null, visitDate: '', phone: '', recipientName: '', sending: false })}
                  className="flex-1 px-4 py-2 rounded-lg border font-medium"
                  style={{borderColor: '#D1D5DB', color: '#374151'}}
                  data-testid="share-sms-cancel-btn"
                >
                  Annulla
                </button>
                <button
                  disabled={shareSmsModal.sending || !shareSmsModal.phone || shareSmsModal.phone.replace(/[^\d+]/g, '').length < 8}
                  onClick={async () => {
                    setShareSmsModal(prev => ({ ...prev, sending: true }));
                    try {
                      const token = localStorage.getItem('token');
                      await axios.post(
                        `${process.env.REACT_APP_BACKEND_URL}/api/referto/${shareSmsModal.visitId}/share-sms`,
                        { phone_number: shareSmsModal.phone, recipient_name: shareSmsModal.recipientName || null },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      toast.success('SMS inviato! Il link sara\' valido 7 giorni');
                      setShareSmsModal({ show: false, visitId: null, visitDate: '', phone: '', recipientName: '', sending: false });
                    } catch (err) {
                      console.error('Errore invio SMS:', err);
                      const msg = err?.response?.data?.detail || 'Errore nell\'invio dell\'SMS';
                      toast.error(msg);
                      setShareSmsModal(prev => ({ ...prev, sending: false }));
                    }
                  }}
                  className="flex-1 px-4 py-2 rounded-lg text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{background: '#10B981'}}
                  data-testid="share-sms-send-btn"
                >
                  {shareSmsModal.sending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Invio...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" /> Invia SMS
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDashboard;
